/**
 * Are You Drunk? / Vision Check – animation class name pools.
 * Pick one at random for image; subset for letters; one per option for answer options.
 */

export const DRUNK_IMAGE_ANIMATIONS = [
  'drunk-img--zoom',
  'drunk-img--rotate',
  'drunk-img--flicker',
  'drunk-img--wobble',
  'drunk-img--pulse',
  'drunk-img--sway',
  'drunk-img--blur',
  'drunk-img--opacity',
] as const;

/** Are You Drunk letter effects – rotate / jump / tilt (spinny drunk feel). */
export const DRUNK_LETTER_ANIMATIONS = [
  'drunk-letter--rotate',
  'drunk-letter--rotate-alt',
  'drunk-letter--rotate-soft',
  'drunk-letter--jump',
  'drunk-letter--jump-alt',
  'drunk-letter--tilt',
  'drunk-letter--flip',
  'drunk-letter--zoom',
] as const;

/**
 * Vision Check letter effects – size + spacing only (no rotate).
 * Distinct from drunk so the two games feel different.
 */
export const VISION_LETTER_ANIMATIONS = [
  'vision-letter--grow',
  'vision-letter--shrink',
  'vision-letter--pulse-size',
  'vision-letter--wide',
  'vision-letter--narrow',
  'vision-letter--track-out',
  'vision-letter--squash',
] as const;

export type DrunkLetterAnimation = (typeof DRUNK_LETTER_ANIMATIONS)[number];
export type VisionLetterAnimation = (typeof VISION_LETTER_ANIMATIONS)[number];
export type LetterAnimationVariant = 'drunk' | 'vision';
export type LetterAnimationClass = DrunkLetterAnimation | VisionLetterAnimation;

export const DRUNK_OPTION_ANIMATIONS = [
  'drunk-option--shake',
  'drunk-option--shake-alt',
  'drunk-option--rotate',
  'drunk-option--rotate-alt',
  'drunk-option--wobble',
  'drunk-option--jitter',
] as const;

/** FNV-1a – better avalanche than the old djb2-style hash for short seeds. */
export function hashString(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Deterministic PRNG (mulberry32) from a 32-bit seed. */
export function createSeededRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function getLetterAnimationsForVariant(
  variant: LetterAnimationVariant
): readonly LetterAnimationClass[] {
  return variant === 'vision' ? VISION_LETTER_ANIMATIONS : DRUNK_LETTER_ANIMATIONS;
}

export function pickRandomImageAnimation(): (typeof DRUNK_IMAGE_ANIMATIONS)[number] {
  return DRUNK_IMAGE_ANIMATIONS[Math.floor(Math.random() * DRUNK_IMAGE_ANIMATIONS.length)];
}

/** Deterministic image animation from a string seed (e.g. question text or questionId). */
export function pickImageAnimationForSeed(seed: string): (typeof DRUNK_IMAGE_ANIMATIONS)[number] {
  return DRUNK_IMAGE_ANIMATIONS[hashString(seed) % DRUNK_IMAGE_ANIMATIONS.length];
}

export function pickRandomLetterAnimation(): DrunkLetterAnimation {
  return DRUNK_LETTER_ANIMATIONS[Math.floor(Math.random() * DRUNK_LETTER_ANIMATIONS.length)];
}

export function pickRandomOptionAnimation(): (typeof DRUNK_OPTION_ANIMATIONS)[number] {
  return DRUNK_OPTION_ANIMATIONS[Math.floor(Math.random() * DRUNK_OPTION_ANIMATIONS.length)];
}

/** Deterministic option animation per index (for stable per-question option wobbles). */
export function pickOptionAnimationForIndex(seed: string | undefined, index: number): (typeof DRUNK_OPTION_ANIMATIONS)[number] {
  return DRUNK_OPTION_ANIMATIONS[hashString(`${seed ?? ''}-${index}`) % DRUNK_OPTION_ANIMATIONS.length];
}

/** Deterministic delay in seconds (0–0.8) for option animation. */
export function pickOptionDelayForIndex(seed: string | undefined, index: number): number {
  return (hashString(`${seed ?? ''}-d-${index}`) % 9) / 10; // 0, 0.1, ... 0.8
}
